import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "./color_scheme.js";
import { RoundedRectangle } from "./rounded_rectangle.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { TransformOptions, UIElement } from "./ui_element.js";
import { UIImage } from "./ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "./ui_text_element.js";

export interface SegmentedControlOptions
{
  localization_keys: readonly string[];
  initial_index: number;
  on_select?: ( index: number ) => void;
  font_size: number;
  selected_text_color?: string;
  text_color?: string;
  /** Track art with one slot per segment (equal widths); the selected pill sits inside a slot, `pill_inset` px from its sides. */
  track_img?: ImgID;
  pill_inset?: number;
  /** Pill top and height (px) when the art's visible track is not the full element height (e.g. a baked-in shadow); labels centre on the pill too. */
  pill_y?: number;
  pill_h?: number;
  /** [left, right] px of each slot's visible capsule in the track art, when the slots are not equal thirds of it. */
  slot_bounds?: readonly ( readonly [ number, number ] )[];
  /** [top, bottom] px of the capsules in the track art: the pill is centred on them vertically (pill_y is then ignored). */
  capsule_y?: readonly [ number, number ];
  /**
   * [width, height] of the track art in its own px. When set, pill_inset, pill_y, pill_h,
   * slot_bounds and capsule_y are in art px too, and are scaled to the control here without
   * rounding: LayoutScaler.*FromDesign rounds every value to a whole CSS px, which moved each
   * capsule edge by up to half a CSS px (1.5 device px at dpr 3) and put the pill off centre.
   */
  art_size?: readonly [ number, number ];
}

/** A rect relative to the control, CSS px. */
interface SegmentedRect { x: number; y: number; w: number; h: number }

export class SegmentedControl extends UIElement
{
  private static readonly SWAP_DURATION_MS = 160;
  /** easeOutBack overshoot of the growing pill (0.8 peaks at ~1.03, still inside its capsule). */
  private static readonly GROW_OVERSHOOT = 0.8;
  private static readonly INSET_RATIO = 0.08;

  private index: number;
  private readonly on_select?: ( index: number ) => void;
  private readonly labels: UITextElement[] = [];
  /** One pill per slot: the selected one at scale 1, the rest collapsed to 0. */
  private readonly pills: RoundedRectangle[] = [];
  private readonly pill_scale: number[] = [];
  private readonly scale_from: number[] = [];
  private readonly segment_w: number;
  private readonly inset: number;
  private readonly pill_w: number;
  private readonly pill_y: number;
  private readonly pill_h: number;
  private readonly slotted: boolean;
  private readonly slot_bounds?: readonly ( readonly [ number, number ] )[];
  /** Final pill rect per slot, aligned to device pixels (see alignPill). */
  private readonly pill_rects: SegmentedRect[] = [];
  private readonly layout_dpr: number;
  private readonly selected_text_color: string;
  private readonly text_color: string;
  private swap_start = 0;
  private swapping = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: SegmentedControlOptions,
    ui_options?: TransformOptions
  )
  {
    super( ctx, x, y, width, height, false, false, ui_options );

    const count = Math.max( 1, options.localization_keys.length );
    this.index = Math.min( count - 1, Math.max( 0, options.initial_index ) );
    this.on_select = options.on_select;
    this.selected_text_color = options.selected_text_color ?? ColorScheme.white;
    this.text_color = options.text_color ?? ColorScheme.settings_toggle_knob;

    // Art px → layout px (identity when the options are already in layout px).
    const sx = options.art_size ? width / options.art_size[0] : 1;
    const sy = options.art_size ? height / options.art_size[1] : 1;

    this.slotted = options.track_img !== undefined;
    this.slot_bounds = options.slot_bounds?.length === count
      ? options.slot_bounds.map( ( [ l, r ] ) => [ l * sx, r * sx ] as const )
      : undefined;
    this.inset = options.pill_inset !== undefined ? options.pill_inset * sx : height * SegmentedControl.INSET_RATIO;
    this.segment_w = this.slotted ? width / count : ( width - this.inset * 2 ) / count;
    // One pill width for every slot (the narrowest one), centred in each.
    this.pill_w = this.slot_bounds
      ? Math.min( ...this.slot_bounds.map( ( [ l, r ] ) => r - l ) ) - this.inset * 2
      : this.slotted ? this.segment_w - this.inset * 2 : this.segment_w;
    this.pill_h = options.pill_h !== undefined ? options.pill_h * sy : height - this.inset * 2;
    this.pill_y = options.capsule_y
      ? ( options.capsule_y[0] + options.capsule_y[1] ) * sy / 2 - this.pill_h / 2
      : options.pill_y !== undefined ? options.pill_y * sy : this.inset;
    this.layout_dpr = window.devicePixelRatio || 1;

    const track = options.track_img !== undefined
      ? new UIImage( ctx, 0, 0, width, height, options.track_img )
      : new RoundedRectangle( ctx, 0, 0, width, height, height / 2, ColorScheme.settings_toggle_track );
    track.setClickable( false );
    this.addChild( track );

    for ( let i = 0; i < count; i++ )
      this.pill_rects.push( this.slotted ? this.alignPill( i ) : { x: this.pillX( i ), y: this.pill_y, w: this.pill_w, h: this.pill_h } );

    for ( let i = 0; i < count; i++ )
    {
      const r = this.pill_rects[i];
      const pill = new RoundedRectangle(
        ctx, r.x, r.y, r.w, r.h,
        r.h / 2, ColorScheme.settings_toggle_on
      );
      pill.setClickable( false );
      const scale = i === this.index ? 1 : 0;
      pill.setScaleImmediate( new Vector2( scale, scale ) );
      this.addChild( pill );
      this.pills.push( pill );
      this.pill_scale.push( scale );
      this.scale_from.push( scale );
    }

    options.localization_keys.forEach( ( key, i ) =>
    {
      // Centred on the pill as it is actually drawn (after device-pixel alignment).
      const r = this.pill_rects[i];
      const label = new UITextElement(
        ctx,
        this.slotted ? r.x + r.w / 2 - this.slotW( i ) / 2 : this.slotX( i ),
        this.slotted ? r.y : 0,
        this.slotW( i ),
        this.slotted ? r.h : height,
        {
          localization_key: key,
          font_size: options.font_size,
          font_weight: 700,
          text_color: i === this.index ? this.selected_text_color : this.text_color,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
        }
      );
      label.setClickable( false );
      this.addChild( label );
      this.labels.push( label );

      const hit = new RoundedRectangle(
        ctx, this.slotX( i ), 0, this.slotW( i ), height, 0, "rgba(0,0,0,0)"
      );
      hit.setOnClick( () => this.select( i, true ) );
      this.addChild( hit );
    } );
  }

  /**
   * The pill rect of slot `index`, aligned so it is centred on its capsule on the screen,
   * not just in layout px. In DOM mode every element's offset and size are snapped to
   * device pixels separately (snapToDevicePx), so a pill centred in CSS px could end up a
   * device pixel closer to one side. Here the capsule centre is taken from the track as it
   * is drawn (its snapped size), and the pill's device-px size is picked (±1 px) so that,
   * with its edges on whole device pixels, its centre lands on the capsule centre (at most
   * 0.25 device px off). Pills are separate elements, so their sizes may differ by a pixel.
   */
  private alignPill( index: number ): SegmentedRect
  {
    const dpr = this.layout_dpr;
    const w = this.rect.getW();
    const h = this.rect.getH();
    const rx = Math.round( w * dpr ) / ( w * dpr );
    const ry = Math.round( h * dpr ) / ( h * dpr );

    const cx = ( this.pillX( index ) + this.pill_w / 2 ) * dpr * rx;
    const cy = ( this.pill_y + this.pill_h / 2 ) * dpr * ry;
    const [ x0, pw ] = SegmentedControl.alignSpan( cx, this.pill_w * dpr * rx );
    const [ y0, ph ] = SegmentedControl.alignSpan( cy, this.pill_h * dpr * ry );
    return { x: x0 / dpr, y: y0 / dpr, w: pw / dpr, h: ph / dpr };
  }

  /** Whole-device-px [start, size] of a span of `size` centred on `centre`, size within ±1 px. */
  private static alignSpan( centre: number, size: number ): [ number, number ]
  {
    const base = Math.round( size );
    let best: [ number, number ] = [ Math.round( centre - base / 2 ), base ];
    let best_err = Infinity;
    for ( const s of [ base, base - 1, base + 1 ] )
    {
      if ( s <= 0 )
        continue;
      const start = Math.round( centre - s / 2 );
      const err = Math.abs( start + s / 2 - centre ) + Math.abs( s - size ) * 1e-3;
      if ( err < best_err )
      {
        best_err = err;
        best = [ start, s ];
      }
    }
    return best;
  }

  private slotX( index: number ): number
  {
    if ( this.slot_bounds )
      return this.slot_bounds[index][0];
    return this.slotted ? this.segment_w * index : this.inset + this.segment_w * index;
  }

  private slotW( index: number ): number
  {
    if ( this.slot_bounds )
      return this.slot_bounds[index][1] - this.slot_bounds[index][0];
    return this.segment_w;
  }

  private pillX( index: number ): number
  {
    return this.slotted ? this.slotX( index ) + ( this.slotW( index ) - this.pill_w ) / 2 : this.slotX( index );
  }

  public getIndex(): number
  {
    return this.index;
  }

  public select( index: number, from_user = false ): void
  {
    if ( index < 0 || index >= this.labels.length )
      return;

    const changed = index !== this.index;
    this.index = index;

    if ( from_user )
      void audioController.playSound( AudioClips.UI_CLICK );

    // The old pill shrinks away, the tapped one grows in. Scaled from updateBasic, not with
    // a TransformScale: in DOM mode that becomes a WAAPI animation, and while it runs Chrome
    // assumes it overlaps everything painted after it, promoting each later element of the
    // dialog to its own layer and dropping it back afterwards. Their re-raster made
    // everything below the control jump.
    for ( let i = 0; i < this.pills.length; i++ )
      this.scale_from[i] = this.pill_scale[i];
    this.swap_start = performance.now();
    this.swapping = true;

    this.labels.forEach( ( label, i ) =>
      label.setTextColor( i === index ? this.selected_text_color : this.text_color ) );

    this.setShouldBeRendered();
    this.invalidateSubtree();

    if ( changed && from_user && this.on_select )
      this.on_select( index );
  }

  protected updateBasic( now: number ): void
  {
    if ( this.swapping )
    {
      const t = Math.min( 1, Math.max( 0, ( now - this.swap_start ) / SegmentedControl.SWAP_DURATION_MS ) );
      for ( let i = 0; i < this.pills.length; i++ )
      {
        const from = this.scale_from[i];
        const to = i === this.index ? 1 : 0;
        if ( from === to && this.pill_scale[i] === to )
          continue;
        const eased = to > from ? SegmentedControl.easeOutBack( t ) : t * t;
        const scale = t >= 1 ? to : Math.max( 0, from + ( to - from ) * eased );
        this.pill_scale[i] = scale;
        this.pills[i].setScaleImmediate( new Vector2( scale, scale ) );
      }
      if ( t >= 1 )
      {
        this.swapping = false;
      }
    }

    super.updateBasic( now );
  }

  private static easeOutBack( t: number ): number
  {
    const c = SegmentedControl.GROW_OVERSHOOT;
    const u = t - 1;
    return 1 + ( c + 1 ) * u * u * u + c * u * u;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "SegmentedControl";
  }
}
